import React, { useMemo, useState } from 'react';
import {
  AddUniversal,
  MoreHorizontalUniversal,
  TrashCanOutlined,
  WarningAltOutlined,
} from '@duxton-web/icons';
import {
  Button,
  Checkbox,
  ConfigProvider,
  Divider,
  Flex,
  Form,
  Popover,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import styled from 'styled-components';

import Service from '@/lib/App';
import { Constraint, ConstraintType } from '@/lib/PlannerDTOs';
import { ExperimentAssignmentStrategy } from '@/pages/ExperimentFormV2/helper/consts';

import '@/pages/ExperimentFormV2/common/ExperimentConstraint.css';

import { useParams } from 'react-router-dom';

import ConstraintEditor, {
  getProperlyInstrumentedFacetList,
  useVariableStatsQuery,
} from '@/common/ConstraintsEditor/Constraint';
import { ConstraintOperator } from '@/common/ConstraintsEditor/Constraint/facetMap';
import { isProduction } from '@/common/environment';
import { FACETS } from '@/constants/facets';
import {
  getAssignmentStrategy,
  getVariables,
  isExperimentTimeConstraint,
  isRestrictByConstraint,
  isViewMode,
} from '@/features/experiment/constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '@/features/experiment/contexts/experiment-context';
import {
  addConstraint,
  deleteConstraint,
  updateConstraint,
} from '@/features/experiment/store/actions/experiment/action-creators';

const { Text } = Typography;

const UNSELECTABLE_CONSTRAINT_FACETS: string[] = ['ts', 'city', 'ctry'];

const getAllowableFacets = (
  assignedStrategy: string,
  variableFacets: string[],
) => {
  const facets = Service.cache.getAllFacets();

  return facets
    .filter(
      (facet) =>
        facet.strategies.length === 0 ||
        facet.strategies.includes(assignedStrategy) ||
        assignedStrategy === ExperimentAssignmentStrategy.TIME_SLICE ||
        assignedStrategy === ExperimentAssignmentStrategy.TIME_SLICE_V2 ||
        assignedStrategy === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE ||
        assignedStrategy === ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
    )
    .filter((facet) => variableFacets.includes(facet.name))
    .filter((facet) => !UNSELECTABLE_CONSTRAINT_FACETS.includes(facet.name))
    .map((facet) => {
      return { value: facet.name, label: facet.title };
    });
};

const Constraints = () => {
  const params = useParams<{ action: string }>();
  const action = params.action;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const experimentDispatch = useExperimentDispatchContext();
  const { experiment, configurations, variableFacets } = experimentContext;
  const experimentStrategy = getAssignmentStrategy(configurations);
  const experimentConstraints = experiment?.constraints;
  const variables = getVariables(configurations);
  const variableNames = useMemo(
    () => variables?.map((variable) => variable.name),
    [variables],
  );

  const form = Form.useFormInstance();

  // const [constraints, setConstraints] = useState<Constraint[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string>(null);
  const [isConstraintError, setIsConstraintError] = useState<number>(null);

  const [skipFacetCheck, setSkipFacetCheck] = useState<boolean>(false);

  const variableStats = useVariableStatsQuery(variableNames);
  const properlyInstrumentedFacetList = getProperlyInstrumentedFacetList(
    variableStats,
  ) as string[];

  // Temp fix to only show some operators to users
  const showConstraint = (constraint: Constraint) => {
    let shouldShow: boolean = true;
    switch (constraint.op) {
      case ConstraintOperator.SLOT.value:
        shouldShow = false;
        break;
      default:
        break;
    }

    if (!shouldShow) return false;

    if (constraint.type === ConstraintType.Targeting) {
      shouldShow = false;
    }

    return shouldShow;
  };

  const displayableConstraints = experimentConstraints?.filter((constraint) => {
    return (
      showConstraint(constraint) &&
      ![FACETS.Timestamp, FACETS.City, FACETS.Country, FACETS.Region].includes(
        constraint.target,
      )
    );
  });

  const shouldDisplayFacetBanner = (constraints: Constraint[]) => {
    let shouldDisplay = false;

    if (!constraints?.length) return shouldDisplay;

    if (!isProduction) return shouldDisplay;

    for (let constraint of constraints) {
      if (!constraint?.target) continue;

      if (
        !properlyInstrumentedFacetList.includes(constraint.target) &&
        constraint.target !== FACETS.Timestamp
      ) {
        shouldDisplay = true;
      }
    }

    return shouldDisplay;
  };

  const displayImproperlyInstrumentedFacetBanner = shouldDisplayFacetBanner(
    displayableConstraints,
  );

  const handleAddConstraint = (whichType: ConstraintType) => {
    let defaultConstraint: Constraint = {
      target: '',
      op: '',
      type: ConstraintType.Standard,
      value: '',
    };

    switch (whichType) {
      case ConstraintType.CustomString:
        defaultConstraint = Object.assign(defaultConstraint, {
          type: ConstraintType.CustomString,
        });
        break;
      case ConstraintType.CustomInt:
        defaultConstraint = Object.assign(defaultConstraint, {
          type: ConstraintType.CustomInt,
        });
        break;
      default:
        break;
    }

    experimentDispatch(addConstraint(defaultConstraint));

    setIsConstraintError(null);
  };

  const handleRemoveConstraint = (index: number) => {
    setIsConstraintError(null);

    experimentDispatch(deleteConstraint(index));
  };

  const handleUpdateConstraint = (index: number, constraint: Constraint) => {
    experimentDispatch(updateConstraint(index, constraint));
  };

  if (!displayableConstraints?.length && readOnly) return null;

  return (
    <>
      <Divider
        orientation="left"
        orientationMargin={0}
        style={{ marginTop: 8, marginBottom: 8 }}
      >
        <Tag color="#3D3D3D" style={{ borderRadius: 8 }}>
          Constraints
        </Tag>
      </Divider>

      <Form.List name="constraints">
        {(constraints) =>
          constraints?.map((constraint) => {
            const constraintData = form.getFieldValue([
              'constraints',
              constraint.name,
            ]);

            if (isRestrictByConstraint(constraintData)) {
              return null;
            }

            if (isExperimentTimeConstraint(constraintData)) {
              return null;
            }

            return (
              <Flex vertical gap={8} key={constraint.key}>
                <ConstraintRow>
                  {readOnly && showConstraint(constraintData) && (
                    <>
                      <ConstraintTag
                        className={
                          constraint.key === isConstraintError
                            ? 'antd-error-state'
                            : ''
                        }
                      >
                        AND
                      </ConstraintTag>
                      <ConstraintEditor
                        index={constraint.name}
                        isCustomConstraint={
                          constraintData.type !== ConstraintType.Standard
                        }
                        allowedFacets={getAllowableFacets(
                          experimentStrategy,
                          variableFacets,
                        )}
                        hasError={constraint.name === isConstraintError}
                        constraint={constraintData}
                        onChange={handleUpdateConstraint}
                        disabled={readOnly}
                        showFacetStatus
                        variableNames={variableNames}
                      />
                    </>
                  )}
                  {!readOnly && showConstraint(constraintData) && (
                    <>
                      <ConstraintTag
                        className={
                          constraint.key === isConstraintError
                            ? 'antd-error-state'
                            : ''
                        }
                      >
                        AND
                      </ConstraintTag>
                      <ConstraintEditor
                        index={constraint.name}
                        isCustomConstraint={
                          constraintData.type !== ConstraintType.Standard
                        }
                        allowedFacets={getAllowableFacets(
                          experimentStrategy,
                          variableFacets,
                        )}
                        hasError={constraint.name === isConstraintError}
                        constraint={constraintData}
                        onChange={handleUpdateConstraint}
                        disabled={readOnly}
                        showFacetStatus
                        variableNames={variableNames}
                      />
                      <ConfigProvider
                        theme={{
                          token: {
                            fontWeightStrong: 400,
                          },
                          components: {
                            Button: {
                              onlyIconSize: 14,
                              defaultBorderColor: '#bfbfbf',
                            },
                          },
                        }}
                      >
                        <Button
                          shape="default"
                          className={
                            constraint.name === isConstraintError
                              ? 'antd-error-state-btn'
                              : ''
                          }
                          icon={<TrashCanOutlined />}
                          onClick={() =>
                            handleRemoveConstraint(constraint.name)
                          }
                          onMouseOver={() => {
                            setIsConstraintError(constraint.name);
                          }}
                          onMouseOut={() => {
                            setIsConstraintError(null);
                          }}
                        />
                      </ConfigProvider>
                    </>
                  )}
                </ConstraintRow>
              </Flex>
            );
          })
        }
      </Form.List>

      {error && <Text type="danger">{error}</Text>}

      {!readOnly && (
        <ConstraintRow>
          <ConstraintTag>AND</ConstraintTag>
          <ConfigProvider
            theme={{
              token: {
                fontWeightStrong: 400,
              },
              components: {
                Button: {
                  contentFontSize: 14,
                  defaultBorderColor: '#bfbfbf',
                },
              },
            }}
          >
            <Tooltip
              title={
                !experimentStrategy &&
                'Please select an assignment strategy first!'
              }
              placement="bottom"
            >
              <Button
                shape="default"
                disabled={!experimentStrategy}
                icon={<AddUniversal />}
                onClick={() => handleAddConstraint(null)}
              >
                Add Constraint
              </Button>
            </Tooltip>

            <Popover
              content={
                <Flex vertical gap={8}>
                  <Button
                    shape="default"
                    ghost
                    icon={<AddUniversal />}
                    onClick={() =>
                      handleAddConstraint(ConstraintType.CustomString)
                    }
                  >
                    Custom Constraint (String)
                  </Button>

                  <Button
                    ghost
                    shape="default"
                    icon={<AddUniversal />}
                    onClick={() =>
                      handleAddConstraint(ConstraintType.CustomInt)
                    }
                  >
                    Custom Constraint (Integer)
                  </Button>
                </Flex>
              }
              trigger="click"
            >
              <Button
                shape="default"
                disabled={!experimentStrategy}
                icon={<MoreHorizontalUniversal />}
              />
            </Popover>
          </ConfigProvider>
        </ConstraintRow>
      )}

      {!readOnly && displayImproperlyInstrumentedFacetBanner && (
        <>
          <FacetCheckBanner>
            <Text strong>
              Some constraint facets may not be implemented in code
            </Text>
            <br />
            <Text style={{ color: '#707070', fontWeight: 400 }}>
              There is no data available for the facets of the constraints
              marked in (<WarningAltOutlined style={{ color: '#faad14' }} />
              ). If they are not implemented in code, these facets will have no
              effect.
            </Text>
            <SkipFacetCheck>
              <Form.Item
                name="facetCheck"
                style={{ marginBottom: 0 }}
                rules={[
                  {
                    required: true,
                  },
                  () => ({
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    validator(_, __) {
                      if (
                        displayImproperlyInstrumentedFacetBanner &&
                        !skipFacetCheck
                      ) {
                        return Promise.reject(
                          new Error('Please select the checkbox'),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                valuePropName="checked"
              >
                <Checkbox
                  onChange={(e) => {
                    setSkipFacetCheck(e.target.checked);
                  }}
                >
                  <Text strong>Skip Facet Check</Text>
                </Checkbox>
              </Form.Item>
            </SkipFacetCheck>
          </FacetCheckBanner>
        </>
      )}
    </>
  );
};

const ConstraintRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px 0;
  align-items: stretch;
`;

const ConstraintTag = styled(Tag)`
  border-radius: 8px;
  background-color: #f5f5f5;
  color: #3d3d3d;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  margin-inline-end: 0px;
`;

const FacetCheckBanner = styled.div`
  min-width: 100%;
  min-height: 74px;
  background: #fff9c0;
  border: 1px solid#FFD933;
  border-radius: 12px;
  padding: 14px 16px;
  margin-top: 8px;
`;

const SkipFacetCheck = styled.div`
  min-width: 100%;
  margin-top: 4px;
`;

export default Constraints;
